import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../../utils/api';
import { money as fmtMoney, currencySymbol, fmtDateTime, localeDate, localeNumber, tz } from '../../utils/region';

// ── Monthly Income/Expense Breakdown (owner 2026-08-17, "Expense breakdown.docx") ────────────────
// Category rows × Jan–Dec columns, mirroring the Fit Koh Samui Excel. Amounts auto-classify by the
// partner's 13-digit tax ID (partner default category → line-level override wins); Salary / SSO /
// Rental rows auto-pull from the same sources as the Half-year P&L so totals reconcile.
// "Assign categories" = per-partner dropdowns + per-line dropdowns for unmatchable rows;
// the gear manages the category list (every tax operator, no extra permission — owner ruling ④).

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const money = (n: number) => n ? fmtMoney(n) : '';

export default function ExpenseBreakdownPanel({ client, onBack }: { client: { id: string; company_name?: string; client_name?: string }; onBack: () => void }) {
  const nowY = new Date().getFullYear();
  const [year, setYear] = useState(nowY);
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [cats, setCats] = useState<any[]>([]);
  const [assignOpen, setAssignOpen] = useState(false);
  const [catsOpen, setCatsOpen] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  // View filters (owner 2026-08-19): quarter toggles (any combination; all-off = all-on) and a
  // hide-empty-rows switch judged on the REAL values of the visible months. Display-only.
  const [quarters, setQuarters] = useState<boolean[]>([true, true, true, true]);
  const [hideEmpty, setHideEmpty] = useState(false);
  // AI projections (owner 2026-08-19): greyed estimates for empty future months, generated on
  // demand and cached server-side. NEVER part of any total — see ebProjection.ts for the rules.
  const [projOn, setProjOn] = useState(false);
  const [proj, setProj] = useState<any[]>([]);
  const [projAt, setProjAt] = useState(0);
  // The run is keyed to the view that STARTED it: a POST takes 40-100s and the year selector stays
  // live, so a global boolean parks the spinner on whatever grid happens to be on screen when it
  // finishes — or loses it entirely when the Reset button unmounts (review 2026-08-23).
  const [busy, setBusy] = useState<null | { key: string; reset: boolean }>(null);
  const canProject = year >= nowY;   // a past year has nothing left to project
  // Excel-style cell notes (owner 2026-08-19): right-click a figure or a row name to leave one; a
  // corner marker shows it exists and the text appears on hover. Internal only — never emailed.
  const [notes, setNotes] = useState<any[]>([]);
  const [noteEdit, setNoteEdit] = useState<null | { categoryId: string; month: number; label: string; body: string; x: number; y: number }>(null);
  // Double-click a figure → what makes it up (invoices / manual entries / the AUTO source).
  const [cellOpen, setCellOpen] = useState<null | { categoryId: string; month: number; name: string }>(null);
  // A note shows on HOVER as a styled bubble (owner 2026-08-19) — the native title tooltip waits a
  // second, truncates and cannot carry the author line.
  const [hoverNote, setHoverNote] = useState<null | { body: string; byName: string; updatedAt: number; x: number; y: number }>(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([api.ebReport(client.id, year), api.ebCategories()])
      .then(([d, c]: any[]) => { setData(d); setCats(c.rows || []); })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [client.id, year]);
  useEffect(() => { load(); }, [load]);
  // Guard against stale responses: projections are keyed client+year, and a slow response from a
  // PREVIOUS view must never render into the current grid (review 2026-08-19).
  const viewRef = React.useRef('');
  const viewKey = `${client.id}:${year}`;
  viewRef.current = viewKey;
  const projBusy = !!busy;                              // one run at a time, as before
  const busyHere = !!busy && busy.key === viewKey;      // ...but is it THIS grid's run?
  const resetting = busyHere && !!busy?.reset;
  // A legitimately empty result must not re-trigger a billed run every time the chip is flicked.
  // Set on success only, so a failed call still retries (review 2026-08-23).
  const triedRef = React.useRef<Record<string, boolean>>({});
  const loadNotes = useCallback(() => {
    api.ebComments(client.id, year).then((d: any) => setNotes(d.rows || [])).catch(() => setNotes([]));
  }, [client.id, year]);
  useEffect(() => { loadNotes(); }, [loadNotes]);
  useEffect(() => {
    const key = `${client.id}:${year}`;
    setProj([]); setProjAt(0);
    api.ebProjections(client.id, year)
      .then((d: any) => { if (viewRef.current === key) { setProj(d.rows || []); setProjAt(d.generatedAt || 0); } })
      .catch(() => { /* cleared above */ });
  }, [client.id, year]);

  const r2l = (n: number) => Math.round((Number(n) || 0) * 100) / 100;
  // Visible months from the quarter toggles; turning everything off equals everything on.
  const visMonths = React.useMemo(() => {
    const anyOn = quarters.some(Boolean);
    const qs = anyOn ? quarters : [true, true, true, true];
    const out: number[] = [];
    for (let m = 1; m <= 12; m++) if (qs[Math.floor((m - 1) / 3)]) out.push(m);
    return out;
  }, [quarters]);
  const projMap = React.useMemo(() => {
    const m = new Map<string, number>();
    if (projOn && canProject) for (const p of proj) m.set(`${p.categoryId}:${p.month}`, p.amount);
    return m;
  }, [proj, projOn, canProject]);
  // Which estimates an operator typed themselves (owner 2026-08-21) — shown with a dotted underline
  // so a corrected figure is distinguishable from a machine guess at a glance.
  const projSrc = React.useMemo(() => {
    const m = new Map<string, string>();
    for (const p of proj) m.set(`${p.categoryId}:${p.month}`, p.source || 'ai');
    return m;
  }, [proj]);
  // How many estimates the operator typed — gates and labels the "Reset to AI" button, which is
  // pointless (and confusingly identical to Regenerate) when there is nothing of theirs to discard.
  const userCount = React.useMemo(() => proj.filter(p => (p.source || 'ai') === 'user').length, [proj]);

  // ── Typing over an estimate ────────────────────────────────────────────────────────────────────
  // Only a cell with NO real data is editable: an invoice-backed figure is the sum of its documents
  // and is corrected by fixing the document, not by typing here (owner: "不能修改"). What is typed
  // stays an ESTIMATE — grey, and outside every total, exactly like the AI value it replaces.
  // kind 'proj'   — typing over an AI estimate (stays an estimate, outside every total)
  // kind 'manual'  — editing the ONE manual entry behind a yellow cell, in place. Money that counts,
  //                  so it saves through the audited manual-entry endpoint and reloads the report.
  const [editCell, setEditCell] = useState<{ categoryId: string; month: number; name: string; kind: 'proj' | 'manual'; manualId?: string } | null>(null);
  const [editVal, setEditVal] = useState('');
  const startEdit = (categoryId: string, month: number, name: string, current?: number, kind: 'proj' | 'manual' = 'proj', manualId?: string) => {
    setEditCell({ categoryId, month, name, kind, manualId });
    setEditVal(current ? String(current) : '');
  };
  const saveEdit = async () => {
    const c = editCell; if (!c) return;
    setEditCell(null);
    const raw = editVal.trim().replace(/,/g, '');
    const amount = raw === '' ? 0 : Number(raw);
    if (!Number.isFinite(amount) || amount < 0) { alert('Enter a positive amount, or leave it empty to clear.'); return; }

    // ── A manual entry is REAL money: it counts in every total, so it saves through the audited
    // endpoint and the whole report is reloaded rather than patched locally. Emptying the box
    // deletes the entry — the quick way to clear a placeholder once the real invoice arrives, which
    // is exactly what these entries are for (owner 2026-08-21). Deletion is confirmed once.
    if (c.kind === 'manual' && c.manualId) {
      try {
        if (amount > 0) await api.ebManualUpdate(c.manualId, { amount });
        else {
          if (!window.confirm(`Delete this manual entry?\n\n${c.name} · ${MONTH_ABBR[c.month - 1]}\n\nIt will stop counting in every total. This cannot be undone.`)) return;
          await api.ebManualDelete(c.manualId);
        }
        load();
      } catch (e: any) { alert(e.message); }
      return;
    }

    const key = `${c.categoryId}:${c.month}`;
    const before = proj;
    // Optimistic: drop any existing estimate for the cell, add the typed one when it is > 0.
    setProj(ps => [...ps.filter(p => `${p.categoryId}:${p.month}` !== key),
      ...(amount > 0 ? [{ categoryId: c.categoryId, month: c.month, amount, source: 'user' }] : [])]);
    try {
      const r: any = await api.ebSetProjection(client.id, year, c.categoryId, c.month, amount);
      // Clearing a correction hands the cell back to a fresh machine estimate server-side
      // (restored=true). Render it, or the grid shows an empty month while the server holds a value
      // — the operator sees their undo as a hole until they reload (review 2026-08-23).
      if (r?.restored && Number(r.amount) > 0 && viewRef.current === `${client.id}:${year}`) {
        setProj(ps => [...ps.filter(p => `${p.categoryId}:${p.month}` !== key),
          { categoryId: c.categoryId, month: c.month, amount: Number(r.amount), source: 'ai' }]);
      }
    }
    catch (e: any) { setProj(before); alert(e.message); }
  };

  const generate = async (reset = false) => {
    if (projBusy) return;
    const key = viewKey;
    setBusy({ key, reset });
    try {
      const d: any = await api.ebProject(client.id, year, reset);
      if (viewRef.current !== key) return;   // user switched client/year mid-flight
      triedRef.current[key] = true;
      setProj(d.rows || []); setProjAt(d.generatedAt || 0);
      // The server explains itself when it declines to project — say so even when rows came back,
      // or "your typed estimates were kept" never reaches the operator who just pressed Reset.
      if (d.note) window.alert(d.note);
      else if (!d.rows?.length) window.alert('No rows qualify for projection (needs some data this year, with empty months remaining).');
    } catch (e: any) { window.alert(e?.message || 'Projection failed'); }
    finally { setBusy(b => (b && b.key === key ? null : b)); }   // never clear a LATER run's state
  };
  // "Reset to AI" (owner 2026-08-23) — the ONLY action that throws away typed estimates. Toggling
  // the chip and pressing Regenerate both leave them alone, so this one asks first and names the
  // count: the figures are gone once the run finishes and there is no undo.
  const resetToAi = () => {
    if (projBusy || !userCount) return;
    if (!window.confirm(`Discard ${userCount} manual estimate${userCount === 1 ? '' : 's'} and let AI generate fresh values for ${year}?\n\nRecorded figures (invoices, bank, manual entries) are not affected — only the grey estimates you typed.`)) return;
    generate(true);
  };
  const toggleProj = () => {
    if (projBusy) return;   // the chip is inert mid-generation (spinner lives on the Regenerate button)
    if (!projOn && !proj.length && !triedRef.current[viewKey]) { setProjOn(true); generate(false); return; }   // first click generates
    setProjOn(o => !o);
  };

  const sel: React.CSSProperties = { padding: '6px 10px', fontSize: 13, borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)' };
  const chip = (on: boolean): React.CSSProperties => ({ padding: '5px 12px', borderRadius: 999, fontSize: 12, fontWeight: 700, cursor: 'pointer', border: on ? '1px solid var(--accent)' : '1px solid var(--border-strong)', background: on ? 'var(--accent-bg)' : 'var(--bg-surface)', color: on ? 'var(--accent-text)' : 'var(--text-secondary)' });
  // Grey = an estimate; never bold, whatever the black figure beside it weighs (owner 2026-08-19).
  const projCell: React.CSSProperties = { color: 'var(--text-tertiary)', fontStyle: 'italic', fontWeight: 400 };
  // Net / Running P/L keep the profit-or-loss colour even when the figure is an ESTIMATE — a grey
  // number hides whether the month is up or down (owner 2026-08-19). Estimates use a FADED shade of
  // the same green/red, so the basis is still obvious at a glance.
  const plColor = (v: number, projected: boolean) =>
    projected ? (v < 0 ? 'rgba(220,38,38,0.55)' : 'rgba(21,128,61,0.55)') : (v < 0 ? '#DC2626' : '#15803D');
  // 10.5 → 11.5px (Calem, report 66c9d174, 2nd opening: "Do the same for all other menus in the SLF system"), and the
  // line under the headings is an inset shadow where it was a bottom border ("Make sure to include the horizontal gray
  // line for any pages that scroll"). The table collapses its borders, and a collapsed border stays with the table when
  // the sticky heading moves, so the headings lost their line as soon as the table scrolled; a shadow is the cell's own.
  // Then Active Jobs' headings, as every staff list's (report 4bd4ae4e, 2nd opening: "Make all menus in the SLF system
  // look like this: the same height, text color, no bold, horizontal line colors etc."): black, --text-primary, at the
  // regular weight, where they were light grey in bold, on one 16px line over the darker --border-strong line. The
  // shadow lies over the cell's last pixel where a border would sit under it, so 10px above and 11 below, where both
  // were 7, make the band 37px as every list's is. The 11.5px stays; the tables further down follow it.
  const th: React.CSSProperties = { padding: '10px 9px 11px', fontSize: 11.5, fontWeight: 400, color: 'var(--text-primary)', lineHeight: '16px', textTransform: 'uppercase', boxShadow: 'inset 0 -1px 0 var(--border-strong)', textAlign: 'right', whiteSpace: 'nowrap', background: 'var(--bg-surface)', position: 'sticky', top: 0, zIndex: 2 };
  const td: React.CSSProperties = { padding: '6px 9px', fontSize: 12, borderBottom: '1px solid var(--border)', textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };
  const tdName: React.CSSProperties = { ...td, textAlign: 'left', position: 'sticky', left: 0, background: 'var(--bg-surface)', fontWeight: 600, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis' };

  const noteMap = React.useMemo(() => {
    const m = new Map<string, any>();
    for (const n of notes) m.set(`${n.categoryId}:${n.month}`, n);
    return m;
  }, [notes]);
  const saveNote = async (body: string) => {
    if (!noteEdit) return;
    try {
      await api.ebSetComment(client.id, { year, categoryId: noteEdit.categoryId, month: noteEdit.month, body });
      setNoteEdit(null); loadNotes();
    } catch (e: any) { window.alert(e?.message || 'Failed to save note'); }
  };
  // Right-click opens the note editor at the pointer, replacing the browser menu on that cell only.
  const openNote = (e: React.MouseEvent, categoryId: string, month: number, label: string) => {
    e.preventDefault(); e.stopPropagation();
    const cur = noteMap.get(`${categoryId}:${month}`);
    setNoteEdit({ categoryId, month, label, body: cur?.body || '', x: e.clientX, y: e.clientY });
  };
  const noteMark = (categoryId: string, month: number) => {
    const n = noteMap.get(`${categoryId}:${month}`);
    if (!n) return null;
    return <span style={{ position: 'absolute', top: 0, right: 0, width: 0, height: 0, borderTop: '7px solid #DC2626', borderLeft: '7px solid transparent' }} />;
  };
  // Every figure on the grid can carry a note — detail rows, the TOTAL column, and the summary rows
  // (owner 2026-08-19). The summary rows have no category of their own, so they use reserved keys
  // that can never collide with a real category id (those are UUIDs).
  const TOTAL_COL = 13;   // 0 = the row itself, 1-12 = a month, 13 = the year TOTAL column
  // A cell that carries a note shows the note bubble and NOTHING else: the browser's own tooltip
  // renders on top of it and hides what the operator wanted to read (owner 2026-08-19).
  const tipUnlessNote = (categoryId: string, month: number, tip?: string) =>
    (noteMap.get(`${categoryId}:${month}`) ? undefined : tip);
  const notable = (categoryId: string, month: number, label: string) => ({
    onContextMenu: (e: React.MouseEvent) => openNote(e, categoryId, month, label),
    ...noteHover(categoryId, month),
    style: { position: 'relative' as const },
  });

  // Hover handlers for any cell that carries a note; cells without one keep their normal tooltip.
  const noteHover = (categoryId: string, month: number) => {
    const n = noteMap.get(`${categoryId}:${month}`);
    if (!n) return {};
    return {
      onMouseEnter: (e: React.MouseEvent) => setHoverNote({ body: n.body, byName: n.byName || '', updatedAt: n.updatedAt || 0, x: e.clientX, y: e.clientY }),
      onMouseMove: (e: React.MouseEvent) => setHoverNote(h => (h ? { ...h, x: e.clientX, y: e.clientY } : h)),
      onMouseLeave: () => setHoverNote(null),
    };
  };

  // category:month → the manual entries inside that cell (marker * + hover detail).
  const manualMap = React.useMemo(() => {
    const m = new Map<string, any[]>();
    for (const e of (data?.manualEntries || [])) { const k = `${e.categoryId}:${e.month}`; const a = m.get(k) || []; a.push(e); m.set(k, a); }
    return m;
  }, [data]);

  // Rows a section actually shows, and the projections that belong to one of its months. Shared with
  // the Net / Running P/L rows so every grey figure on the page is built from the same cells.
  const visRows = (sec: any): any[] => {
    const all: any[] = sec?.rows || [];
    return hideEmpty ? all.filter((r: any) => visMonths.some(m => r.months[m - 1] !== 0)) : all;
  };
  const projSumOf = (rowsArr: any[], m: number) => r2l(rowsArr.reduce((s: number, r: any) =>
    s + (r.months[m - 1] === 0 && !manualMap.get(`${r.id}:${m}`) ? (projMap.get(`${r.id}:${m}`) || 0) : 0), 0));
  // Net WITH projections for one month (owner 2026-08-19): projected income − projected expense.
  const projNet = (m: number) => r2l(
    (data.income.monthTotals[m - 1] + projSumOf(visRows(data.income), m)) -
    (data.expense.monthTotals[m - 1] + projSumOf(visRows(data.expense), m)));
  const hasProj = (m: number) => projSumOf(visRows(data.income), m) !== 0 || projSumOf(visRows(data.expense), m) !== 0;

  const section = (label: string, sec: any, tone: string, totalKey: string) => {
    const rowsAll: any[] = sec.rows || [];
    const rows = visRows(sec);
    const hiddenCount = rowsAll.length - rows.length;
    // Grey monthly subtotal (real + projections) on the Total row. Two review rules (2026-08-19):
    // count a projection only while its cell is still EMPTY (real data arriving later must not be
    // double-counted), and only over the VISIBLE rows (hidden-empty rows' projections would make a
    // subtotal no visible cell explains). The year-end TOTAL column stays real-only.
    const projSum = (m: number) => projSumOf(rows, m);
    return (
    <>
      <tr>
        <td style={{ ...tdName, background: 'var(--bg-app)', color: tone, fontWeight: 800, fontSize: 12.5 }}>
          {label}{hiddenCount > 0 && <span style={{ marginLeft: 8, fontSize: 10.5, fontWeight: 700, color: 'var(--text-tertiary)' }}>· {hiddenCount} empty row(s) hidden</span>}
        </td>
        <td colSpan={visMonths.length + 1} style={{ background: 'var(--bg-app)', borderBottom: '1px solid var(--border)' }} />
      </tr>
      {rows.map((r: any) => (
        <tr key={r.id} style={r.id === 'unclassified' ? { background: '#FFFBEB' } : undefined}>
          <td onContextMenu={e => openNote(e, r.id, 0, r.name)} {...noteHover(r.id, 0)}
            title={noteMap.get(`${r.id}:0`) ? undefined : 'Right-click to add a note'}
            style={{ ...tdName, position: 'relative', color: r.id === 'unclassified' ? '#92400E' : 'var(--text-primary)', background: r.id === 'unclassified' ? '#FFFBEB' : 'var(--bg-surface)' }}>
            {noteMark(r.id, 0)}
            {r.name}
            {r.auto && <span title="System-calculated from payroll / SSO / rental data — same sources as the P&L" style={{ marginLeft: 5, fontSize: 9, fontWeight: 800, padding: '0 4px', borderRadius: 4, background: 'var(--accent-bg)', color: 'var(--accent-text)' }}>AUTO</span>}
          </td>
          {visMonths.map(m => {
            const v = r.months[m - 1];
            const mans = manualMap.get(`${r.id}:${m}`);
            // No projection in a cell that carries manual entries — even ones netting to 0: the
            // manual tooltip would swallow the projection explanation (review 2026-08-19).
            const pv = !v && !mans ? projMap.get(`${r.id}:${m}`) : undefined;
            // Editable only where nothing real lives: no invoice-backed figure and no manual entry.
            const editable = !v && !mans;
            // ...and an ESTIMATE additionally needs somewhere to live: a past year cannot show one
            // (the whole projection toolbar is hidden there, so the row would be an invisible orphan
            // the operator can never delete), and a run in flight ends by rewriting this client-year,
            // which would swallow the figure without a trace. Both are refused server-side too.
            const canEditProj = editable && canProject && !projBusy;
            // A YELLOW cell can be corrected in place too, but only when there is no ambiguity about
            // WHICH number the typed figure replaces: exactly one manual entry, and nothing
            // invoice-backed sharing the cell. Anything else opens the detail panel, where each
            // entry has its own row (owner-approved plan C, 2026-08-21).
            const manSum = mans ? r2l(mans.reduce((s: number, e: any) => s + (Number(e.amount) || 0), 0)) : 0;
            const manEditable = !!mans && mans.length === 1 && Math.abs(r2l(v) - manSum) < 0.01;
            const mine = editable && projSrc.get(`${r.id}:${m}`) === 'user';
            const editing = editCell?.categoryId === r.id && editCell?.month === m;
            return (
              /* A cell showing a figure keeps double-click for its detail, so an estimate is typed
                 over on the THIRD click; a blank cell has no detail to open, so two clicks do it. */
              <td key={m}
                onContextMenu={e => openNote(e, r.id, m, `${r.name} · ${MONTH_ABBR[m - 1]}`)}
                onClick={e => {
                  if (e.detail !== 3) return;
                  if (editable) { if (canEditProj) startEdit(r.id, m, r.name, pv); }
                  else if (manEditable) startEdit(r.id, m, r.name, mans![0].amount, 'manual', mans![0].id);
                  else if (mans) setCellOpen({ categoryId: r.id, month: m, name: r.name });   // several entries → edit them in the panel
                }}
                onDoubleClick={() => { if (editable && !pv) { if (canEditProj) startEdit(r.id, m, r.name); } else if (!editable) setCellOpen({ categoryId: r.id, month: m, name: r.name }); }}
                {...noteHover(r.id, m)}
                style={{ ...(mans ? { ...td, background: '#FFFBEB' } : td), position: 'relative', cursor: 'default' }}
                title={tipUnlessNote(r.id, m, editing ? undefined
                  : pv ? (mine ? 'Your estimate — replaces the AI value and survives Regenerate. Never counted in totals. Triple-click to change, clear it to drop back to the AI value.'
                               : 'AI projection — an estimate from this year\'s pattern. Not a real entry, never counted in totals. Triple-click to type your own.')
                  : canEditProj ? 'No data — double-click to type an estimate.'
                  : editable ? (canProject ? 'No data — a projection run is in progress; wait for it to finish.'
                                           : 'No data — estimates can only be typed for the current or a future year.')
                  : manEditable ? 'Manual entry — triple-click to change it here, or clear the box to delete it. Double-click for the detail.'
                  : mans ? 'Several manual entries — triple-click to edit them in the detail panel.'
                  : 'Double-click for the detail · right-click to add a note')}>
                {noteMark(r.id, m)}
                {editing ? (
                  <input autoFocus value={editVal} onChange={e => setEditVal(e.target.value)}
                    onBlur={saveEdit}
                    onKeyDown={e => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') setEditCell(null); }}
                    placeholder="0.00"
                    style={{ width: '100%', boxSizing: 'border-box', padding: '1px 3px', textAlign: 'right', fontSize: 12,
                      fontVariantNumeric: 'tabular-nums', border: '1px solid var(--accent)', borderRadius: 4,
                      background: 'var(--bg-app)', color: 'var(--text-primary)' }} />
                ) : (
                  <>{v ? money(v) : pv ? <span style={{ ...projCell, ...(mine ? { textDecoration: 'underline dotted', textUnderlineOffset: 3 } : {}) }}>{money(pv)}</span> : ''}{mans && <span style={{ color: '#B45309', fontWeight: 800 }}> *</span>}</>
                )}
              </td>
            );
          })}
          {(() => {
            const real = r2l(visMonths.reduce((s, m) => s + r.months[m - 1], 0));
            const pj = r2l(visMonths.reduce((s, m) => s + (r.months[m - 1] === 0 && !manualMap.get(`${r.id}:${m}`) ? (projMap.get(`${r.id}:${m}`) || 0) : 0), 0));
            const nb = notable(r.id, TOTAL_COL, `${r.name} · Total`);
            return (
              <td {...nb} style={{ ...td, ...nb.style, fontWeight: 700 }} title={tipUnlessNote(r.id, TOTAL_COL, pj > 0 ? 'Grey = the same total including this row\'s AI estimates. The black figure stays real data only.' : undefined)}>
                {noteMark(r.id, TOTAL_COL)}
                {money(real)}
                {pj > 0 && <div style={{ ...projCell }}>{money(r2l(real + pj))}</div>}
              </td>
            );
          })()}
        </tr>
      ))}
      <tr>
        {(() => { const nb = notable(totalKey, 0, `Total ${label}`); return (
          <td {...nb} style={{ ...tdName, ...nb.style, fontWeight: 800, fontSize: 14 }}>{noteMark(totalKey, 0)}Total {label}</td>
        ); })()}
        {visMonths.map(m => {
          const v = sec.monthTotals[m - 1];
          const ps = projSum(m);
          return (
            <td key={m} {...notable(totalKey, m, `Total ${label} · ${MONTH_ABBR[m - 1]}`)}
              style={{ ...td, position: 'relative', fontWeight: 800, fontSize: 14, borderTop: '2px solid var(--border-strong)' }}
              title={tipUnlessNote(totalKey, m, ps > 0 ? 'This month\'s real + projected subtotal — switch AI Projection off for the real-data figure.' : undefined)}>
              {noteMark(totalKey, m)}
              {/* A projected month shows ONE figure, not two (owner 2026-08-21): the estimate already
                  CONTAINS the real part, so printing the black figure beside it invites reading the pair
                  as two separate amounts. The annual Total column below deliberately keeps both — there
                  the black figure is the year's real total, a number the owner reads on its own. */}
              {ps > 0 ? <span style={{ ...projCell }}>{money(r2l(v + ps))}</span> : (v !== 0 && money(v))}
            </td>
          );
        })}
        {(() => {
          const real = r2l(visMonths.reduce((s, m) => s + sec.monthTotals[m - 1], 0));
          const pj = r2l(visMonths.reduce((s, m) => s + projSum(m), 0));
          return (
            <td {...notable(totalKey, TOTAL_COL, `Total ${label} · Total`)}
              style={{ ...td, position: 'relative', fontWeight: 800, fontSize: 14, borderTop: '2px solid var(--border-strong)' }}
              title={tipUnlessNote(totalKey, TOTAL_COL, pj > 0 ? 'Grey = the same total including the AI estimates. The black figure stays real data only.' : undefined)}>
              {noteMark(totalKey, TOTAL_COL)}
              {money(real)}
              {pj > 0 && <div style={{ ...projCell }}>{money(r2l(real + pj))}</div>}
            </td>
          );
        })()}
      </tr>
    </>
    );
  };

  return (
    <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: '18px 22px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 14, flexShrink: 0 }}>
        <button onClick={onBack} style={{ border: '1px solid var(--border)', background: 'transparent', borderRadius: 8, padding: '5px 10px', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 12 }}>
          <i className="ti ti-arrow-left" style={{ marginRight: 5 }} />Change client</button>
        <span style={{ fontSize: 15, fontWeight: 800, color: 'var(--text-primary)' }}>{data?.client?.name || client.company_name || client.client_name || ''}</span>
        <div style={{ flex: 1 }} />
        {data && data.unclassifiedLines > 0 && (
          <span style={{ fontSize: 11.5, fontWeight: 700, color: '#92400E', background: '#FFFBEB', border: '1px solid #F1C232', borderRadius: 999, padding: '2px 10px' }}>
            {data.unclassifiedLines} unclassified line(s)
          </span>
        )}
        <select value={year} onChange={e => setYear(Number(e.target.value))} style={sel}>
          {Array.from({ length: 5 }, (_, i) => nowY + 1 - i).map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <button onClick={() => setManualOpen(true)} title="Bills with no invoice in the system yet (expense side)" style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid var(--accent)', background: 'transparent', color: 'var(--accent)', fontWeight: 700, fontSize: 12.5, cursor: 'pointer' }}>
          <i className="ti ti-pencil-plus" style={{ marginRight: 5 }} />Manual entries{data?.manualEntries?.length ? ` (${data.manualEntries.length})` : ''}
        </button>
        <button onClick={() => setAssignOpen(true)} style={{ padding: '6px 12px', borderRadius: 8, border: 'none', background: 'var(--accent)', color: '#fff', fontWeight: 700, fontSize: 12.5, cursor: 'pointer' }}>
          <i className="ti ti-category" style={{ marginRight: 5 }} />Assign categories
        </button>
        <button onClick={() => setCatsOpen(true)} title="Manage the category list" style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13 }}>
          <i className="ti ti-settings" />
        </button>
      </div>

      {/* View filters: quarter toggles (any combination — for quarterly / half-year / full-year
          comparison), hide-empty, and the AI-projection switch. All display-only. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 12, flexShrink: 0 }}>
        {['Jan–Mar', 'Apr–Jun', 'Jul–Sep', 'Oct–Dec'].map((q, i) => (
          <button key={q} onClick={() => setQuarters(a => a.map((v, j) => (j === i ? !v : v)))} style={chip(quarters[i])}>{q}</button>
        ))}
        <span style={{ width: 1, height: 20, background: 'var(--border-strong)', margin: '0 2px' }} />
        <button onClick={() => setHideEmpty(h => !h)} style={chip(hideEmpty)} title="Hide rows with no data in the visible months">
          <i className="ti ti-eye-off" style={{ marginRight: 5 }} />Hide empty rows
        </button>
        {canProject && (<>
          <span style={{ width: 1, height: 20, background: 'var(--border-strong)', margin: '0 2px' }} />
          <button onClick={toggleProj} disabled={projBusy} style={{ ...chip(projOn), cursor: projBusy ? 'not-allowed' : 'pointer', opacity: projBusy ? 0.6 : 1 }} title="Greyed AI estimates for empty months of rows that already have data this year. Never counted in any total.">
            <i className="ti ti-sparkles" style={{ marginRight: 5 }} />AI Projection
          </button>
          {projOn && (
            <button onClick={() => generate(false)} disabled={projBusy} style={{ padding: '5px 12px', borderRadius: 999, fontSize: 12, fontWeight: 700, cursor: projBusy ? 'not-allowed' : 'pointer', border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', color: 'var(--text-secondary)', opacity: projBusy ? 0.6 : 1 }}
              title="New AI estimates for the empty months. Estimates you typed yourself are kept — use Reset to AI to drop those too.">
              {busyHere && !resetting ? <><i className="ti ti-loader-2" style={{ display: 'inline-block', animation: 'spin 0.7s linear infinite', marginRight: 5 }} />Generating…</> : <><i className="ti ti-refresh" style={{ marginRight: 5 }} />Regenerate</>}
            </button>
          )}
          {projOn && (userCount > 0 || resetting) && (
            <button onClick={resetToAi} disabled={projBusy} style={{ padding: '5px 12px', borderRadius: 999, fontSize: 12, fontWeight: 700, cursor: projBusy ? 'not-allowed' : 'pointer', border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', color: 'var(--text-secondary)', opacity: projBusy ? 0.6 : 1 }}
              title={`Throw away the ${userCount} estimate${userCount === 1 ? '' : 's'} you typed and let the AI fill those cells again. Recorded figures are untouched.`}>
              {resetting ? <><i className="ti ti-loader-2" style={{ display: 'inline-block', animation: 'spin 0.7s linear infinite', marginRight: 5 }} />Resetting…</> : <><i className="ti ti-wand" style={{ marginRight: 5 }} />Reset to AI ({userCount})</>}
            </button>
          )}
          {projOn && !projBusy && projAt > 0 && (
            <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>generated {fmtDateTime(projAt)}</span>
          )}
        </>)}
      </div>
      {projOn && canProject && proj.length > 0 && (
        <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', background: 'var(--bg-app)', border: '1px dashed var(--border-strong)', borderRadius: 8, padding: '6px 12px', marginBottom: 10, flexShrink: 0 }}>
          <i className="ti ti-sparkles" style={{ marginRight: 5 }} />
          Grey italic values are AI ESTIMATES, built from each row's OWN history: the level is the midpoint of the
          year's average and the last three recorded months, and the month-to-month swing is that row's real swings
          replayed in a shuffled order (±1%, so no two months repeat). A row with a single recorded month has no swing
          to replay and stays flat by design. Triple-click an estimate to type your own; a blank cell takes two clicks. What you type stays yours —
          Regenerate leaves it alone; clear one cell to hand it back, or press Reset to AI to hand back all of them.
          Totals show the real figure with the estimated one in grey beneath it; Net and Running P/L switch over
          entirely while this is on. The Half-year P&L and the VAT ledger are never affected.
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-tertiary)' }}><i className="ti ti-loader" style={{ fontSize: 22, animation: 'spin 1s linear infinite', display: 'inline-block' }} /></div>
      ) : !data ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-tertiary)', fontSize: 13 }}>Failed to load.</div>
      ) : (
        <div style={{ flex: 1, minHeight: 0, overflow: 'auto', border: '1px solid var(--border)', borderRadius: 10, background: 'var(--bg-surface)' }}>
          <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
          <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: visMonths.length === 12 ? 1250 : 420 + visMonths.length * 76 }}>
            <thead><tr>
              <th style={{ ...th, textAlign: 'left', left: 0, zIndex: 3 }}>Description</th>
              {visMonths.map(m => <th key={m} style={th}>{MONTH_ABBR[m - 1]}</th>)}
              <th style={th}>Total</th>
            </tr></thead>
            <tbody>
              {section('Income', data.income, '#15803D', '__total_income')}
              {section('Expense', data.expense, '#B42318', '__total_expense')}
              <tr>
                {(() => { const nb = notable('__net', 0, 'Income − Expense (Net)'); return (
                  <td {...nb} style={{ ...tdName, ...nb.style, fontWeight: 800, fontSize: 12.5 }}>{noteMark('__net', 0)}Income − Expense (Net)</td>
                ); })()}
                {visMonths.map(m => {
                  // With projections ON this reads on the projected basis — the real-data net for a
                  // month whose income has not been invoiced yet is a large negative that looks
                  // like an error beside it (owner 2026-08-19). One figure, one basis.
                  const shown = hasProj(m) ? projNet(m) : data.net[m - 1];
                  const isProj = hasProj(m);
                  return (
                    <td key={m} {...notable('__net', m, `Net · ${MONTH_ABBR[m - 1]}`)}
                      style={{ ...td, position: 'relative', fontWeight: 800, color: plColor(shown, isProj), borderTop: '2px solid var(--border-strong)', ...(isProj ? { fontStyle: 'italic' } : {}) }}
                      title={tipUnlessNote('__net', m, isProj ? 'Includes AI estimates — switch AI Projection off for the real-data net.' : undefined)}>
                      {noteMark('__net', m)}{money(shown)}
                    </td>
                  );
                })}
                {(() => {
                  const real = r2l(visMonths.reduce((s, m) => s + data.net[m - 1], 0));
                  const withP = r2l(visMonths.reduce((s, m) => s + (hasProj(m) ? projNet(m) : data.net[m - 1]), 0));
                  const shown = withP !== real ? withP : real;
                  return (
                    <td {...notable('__net', TOTAL_COL, 'Net · Total')}
                      style={{ ...td, position: 'relative', fontWeight: 800, color: plColor(shown, withP !== real), borderTop: '2px solid var(--border-strong)', ...(withP !== real ? { fontStyle: 'italic' } : {}) }}
                      title={tipUnlessNote('__net', TOTAL_COL, withP !== real ? 'Includes AI estimates — switch AI Projection off for the real-data net.' : undefined)}>
                      {noteMark('__net', TOTAL_COL)}{money(shown)}
                    </td>
                  );
                })()}
              </tr>
              {/* Running P/L: ALWAYS cumulative from January (YTD, real data only) no matter which
                  quarters are visible — the accountant's running total (owner ruling 2026-08-19). */}
              <tr>
                <td {...notable('__running', 0, 'Running Profit/Loss')}
                  style={{ ...tdName, position: 'relative', fontWeight: 800, fontSize: 12.5 }} title="Cumulative Net from January (YTD) — unaffected by the quarter filter">
                  {noteMark('__running', 0)}Running Profit/Loss
                  {/* When the view doesn't start at January, the bracket shows the hidden earlier
                      months' net that the running figures carry in (owner 2026-08-19). */}
                  {visMonths[0] > 1 && (() => {
                    const prior = r2l(data.net.slice(0, visMonths[0] - 1).reduce((s: number, v: number) => s + v, 0));
                    const label = visMonths[0] === 2 ? MONTH_ABBR[0] : `${MONTH_ABBR[0]}–${MONTH_ABBR[visMonths[0] - 2]}`;
                    return <span style={{ marginLeft: 7, fontSize: 10.5, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: 'var(--bg-app)', border: '1px solid var(--border-strong)', color: prior < 0 ? '#DC2626' : 'var(--text-secondary)' }}>{label} ({money(prior) || '0.00'})</span>;
                  })()}
                </td>
                {visMonths.map(m => {
                  let run = 0, runP = 0, anyP = false;
                  for (let k = 1; k <= m; k++) {
                    run += data.net[k - 1];
                    if (hasProj(k)) { runP += projNet(k); anyP = true; } else runP += data.net[k - 1];
                  }
                  run = r2l(run); runP = r2l(runP);
                  const shown = anyP ? runP : run;
                  return (
                    <td key={m} {...notable('__running', m, `Running P/L · ${MONTH_ABBR[m - 1]}`)}
                      style={{ ...td, position: 'relative', fontWeight: 800, color: plColor(shown, anyP), ...(anyP ? { fontStyle: 'italic' } : {}) }}
                      title={tipUnlessNote('__running', m, anyP ? 'Includes AI estimates — switch AI Projection off for the real-data running total.' : undefined)}>
                      {noteMark('__running', m)}{money(shown)}
                    </td>
                  );
                })}
                <td style={td} />
              </tr>
              {/* ── Estimated Corporate Income Tax (owner 2026-08-21) ────────────────────────────
                  ONE figure, under the last visible month — that cell of Running P/L is the profit
                  accumulated from January, so in the full-year view it is the year's profit. A flat
                  20% of it, and 0 on a loss (owner: "亏损时，企业所得税为0").
                  ⚠️ Thailand taxes SMEs on a BANDED scale, not a single rate; the owner chose a flat
                  20% for now ("先不分档") and the row name carries the rate so nobody reads it as the
                  filed figure. Display-only — it enters no total, no P&L and no tax ledger. */}
              <tr>
                <td {...notable('__cit', 0, 'Estimated Corporate Income Tax')}
                  style={{ ...tdName, position: 'relative', fontWeight: 800, fontSize: 12.5 }}
                  title="A flat 20% of the profit shown to its right — an estimate for planning, not a filed figure. Thailand's SME rates are banded; this row is not. A loss shows 0.">
                  {noteMark('__cit', 0)}Estimated Corporate Income Tax (20%)
                </td>
                {visMonths.map((m, idx) => {
                  if (idx !== visMonths.length - 1) return <td key={m} style={td} />;
                  let run = 0, runP = 0, anyP = false;
                  for (let k = 1; k <= m; k++) {
                    run += data.net[k - 1];
                    if (hasProj(k)) { runP += projNet(k); anyP = true; } else runP += data.net[k - 1];
                  }
                  const profit = r2l(anyP ? runP : run);
                  const cit = profit > 0 ? r2l(profit * 0.20) : 0;
                  return (
                    <td key={m} {...notable('__cit', m, `Estimated CIT · ${MONTH_ABBR[m - 1]}`)}
                      style={{ ...td, position: 'relative', fontWeight: 800, ...(anyP ? { ...projCell, fontWeight: 700 } : {}) }}
                      title={tipUnlessNote('__cit', m, profit <= 0
                        ? 'No corporate income tax on a loss.'
                        : anyP ? '20% of the running profit INCLUDING AI estimates — switch AI Projection off for the real-data figure.'
                               : '20% of the profit accumulated from January.')}>
                      {noteMark('__cit', m)}{money(cit)}
                    </td>
                  );
                })}
                <td style={td} />
              </tr>
            </tbody>
          </table>
          {!!(data.manualEntries?.length) && (
            <div style={{ padding: '8px 14px', fontSize: 11.5, color: '#92400E', background: '#FFFBEB', borderTop: '1px solid var(--border)' }}>
              <i className="ti ti-alert-triangle" style={{ marginRight: 5 }} />
              This year includes {currencySymbol()}{money(data.manualTotal) || '0.00'} of manual entries (cells marked *, where their months are visible) — they are
              NOT part of the Half-year P&L, and must be deleted by hand once the real invoices reach the system, or the amount double-counts.
            </div>
          )}
        </div>
      )}

      {hoverNote && !noteEdit && (
        <div style={{ position: 'fixed', left: Math.min(hoverNote.x + 14, window.innerWidth - 300), top: Math.min(hoverNote.y + 14, window.innerHeight - 130), zIndex: 1300, pointerEvents: 'none', width: 280, background: '#FFFDF0', border: '1px solid #E0C97F', borderRadius: 8, boxShadow: 'var(--shadow-md)', padding: '8px 10px' }}>
          <div style={{ fontSize: 12.5, color: 'var(--text-primary)', whiteSpace: 'pre-wrap', lineHeight: 1.45 }}>{hoverNote.body}</div>
          <div style={{ fontSize: 10.5, color: 'var(--text-tertiary)', marginTop: 5 }}>
            {hoverNote.byName}{hoverNote.updatedAt ? ` · ${new Date(hoverNote.updatedAt * 1000).toLocaleDateString(localeDate(), { timeZone: tz() })}` : ''}
          </div>
        </div>
      )}
      {noteEdit && <NoteEditor edit={noteEdit} onCancel={() => setNoteEdit(null)} onSave={saveNote} />}
      {cellOpen && (
        <CellDetailModal clientId={client.id} year={year} month={cellOpen.month} categoryId={cellOpen.categoryId}
          name={cellOpen.name} onClose={() => setCellOpen(null)} onChanged={load} />
      )}
      {assignOpen && <AssignModal clientId={client.id} year={year} cats={cats} onClose={() => { setAssignOpen(false); load(); }} />}
      {catsOpen && <CategoriesModal onClose={() => { setCatsOpen(false); load(); }} />}
      {manualOpen && <ManualEntriesModal clientId={client.id} year={year} cats={cats} onClose={() => { setManualOpen(false); load(); }} />}
    </div>
  );
}

// ── Excel-style cell note (owner 2026-08-19) ─────────────────────────────────────────────────────
// Opens where the pointer was, like a spreadsheet comment. Saving an empty body deletes the note.
function NoteEditor({ edit, onCancel, onSave }: { edit: any; onCancel: () => void; onSave: (b: string) => void | Promise<void> }) {
  const [body, setBody] = useState(edit.body || '');
  const [busy, setBusy] = useState(false);
  const ref = React.useRef<HTMLTextAreaElement | null>(null);
  useEffect(() => { ref.current?.focus(); }, []);
  const save = async () => { setBusy(true); try { await onSave(body.trim()); } finally { setBusy(false); } };
  // Keep the popup on screen when the click was near the right/bottom edge.
  const left = Math.min(edit.x, window.innerWidth - 320);
  const top = Math.min(edit.y, window.innerHeight - 220);
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1200 }} onMouseDown={e => e.target === e.currentTarget && onCancel()}>
      <div style={{ position: 'absolute', left, top, width: 300, background: '#FFFDF0', border: '1px solid #E0C97F', borderRadius: 8, boxShadow: 'var(--shadow-md)', padding: 10 }}>
        <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--text-secondary)', marginBottom: 6 }}>{edit.label}</div>
        <textarea ref={ref} value={body} onChange={e => setBody(e.target.value)} rows={4}
          onKeyDown={e => { if (e.key === 'Escape') onCancel(); if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) save(); }}
          placeholder="Note (visible in this page only)…"
          style={{ width: '100%', fontSize: 12.5, padding: '6px 8px', borderRadius: 6, border: '1px solid #E0C97F', background: '#fff', color: 'var(--text-primary)', boxSizing: 'border-box', resize: 'vertical' }} />
        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', marginTop: 7 }}>
          <button onClick={onCancel} style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', fontSize: 12, cursor: 'pointer' }}>Cancel</button>
          <button onClick={save} disabled={busy} style={{ padding: '4px 12px', borderRadius: 6, border: 'none', background: 'var(--accent)', color: '#fff', fontWeight: 700, fontSize: 12, cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.6 : 1 }}>
            {busy ? '…' : (edit.body ? 'Save' : 'Add')}
          </button>
        </div>
        {edit.body && <div style={{ fontSize: 10.5, color: 'var(--text-tertiary)', marginTop: 5 }}>Clear the text and save to delete this note.</div>}
      </div>
    </div>
  );
}

// ── What makes up one figure (owner 2026-08-19) — double-click drill-down ────────────────────────
// The server resolves the lines with the SAME classification the grid uses, so the list always adds
// up to the figure it explains.
function CellDetailModal({ clientId, year, month, categoryId, name, onClose, onChanged }: {
  clientId: string; year: number; month: number; categoryId: string; name: string; onClose: () => void; onChanged?: () => void;
}) {
  const [d, setD] = useState<any>(null);
  const [err, setErr] = useState('');
  // Editing a manual entry from here is the fallback path for cells the grid cannot edit in place —
  // several entries in one cell, or manual money sharing a cell with invoice lines (owner-approved
  // plan C, 2026-08-21). Each entry has its own row, so there is never a question of which one moved.
  const [mEdit, setMEdit] = useState<{ id: string; amount: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const reload = () => api.ebCell(clientId, year, month, categoryId).then((r: any) => setD(r)).catch(() => {});
  const saveManual = async (id: string, raw: string) => {
    const amount = Number(String(raw).trim().replace(/,/g, ''));
    if (!Number.isFinite(amount) || amount <= 0) { alert('Enter a positive amount. To remove the entry use the delete button.'); return; }
    setBusy(true);
    try { await api.ebManualUpdate(id, { amount }); setMEdit(null); await reload(); onChanged?.(); }
    catch (e: any) { alert(e.message); } finally { setBusy(false); }
  };
  const delManual = async (m: any) => {
    if (!window.confirm(`Delete this manual entry?\n\n${name} · ${MONTH_ABBR[month - 1]} · ${currencySymbol()}${money(m.amount)}\n\nIt will stop counting in every total. This cannot be undone.`)) return;
    setBusy(true);
    try { await api.ebManualDelete(m.id); await reload(); onChanged?.(); }
    catch (e: any) { alert(e.message); } finally { setBusy(false); }
  };
  useEffect(() => {
    let alive = true;
    api.ebCell(clientId, year, month, categoryId)
      .then((r: any) => alive && setD(r))
      .catch((e: any) => alive && setErr(e?.message || 'Failed to load'));
    return () => { alive = false; };
  }, [clientId, year, month, categoryId]);
  // 10.5 → 11.5px (Calem, report 66c9d174, 2nd opening — see the breakdown table's th). Black, regular weight, 10px above
  // and below on a 16px line, white, over the darker --border-strong line (report 4bd4ae4e — see that th too).
  const th2: React.CSSProperties = { padding: '10px 8px', fontSize: 11.5, fontWeight: 400, color: 'var(--text-primary)', lineHeight: '16px', textTransform: 'uppercase', borderBottom: '1px solid var(--border-strong)', textAlign: 'left', whiteSpace: 'nowrap', background: 'var(--bg-surface)' };
  const td2: React.CSSProperties = { padding: '5px 8px', fontSize: 12.5, borderBottom: '1px solid var(--border)' };
  const numTd: React.CSSProperties = { ...td2, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };
  const open = () => window.open(`/m/tax?client=${encodeURIComponent(clientId)}&year=${year}&month=${month}`, '_blank');
  return (
    <div onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1150, padding: 18 }}>
      <div style={{ width: 'min(860px, 100%)', maxHeight: '86vh', overflowY: 'auto', background: 'var(--bg-surface)', borderRadius: 14, border: '1px solid var(--border)', padding: 20, boxShadow: 'var(--shadow-md)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 14.5, fontWeight: 800 }}>{name} — {MONTH_ABBR[month - 1]} {year}</div>
          <button onClick={onClose} style={{ width: 28, height: 28, borderRadius: 8, border: 'none', background: 'var(--c-gray-100)', color: 'var(--text-secondary)', cursor: 'pointer' }}><i className="ti ti-x" /></button>
        </div>
        {err ? <div style={{ padding: 16, color: '#B42318', fontSize: 12.5 }}>{err}</div>
          : !d ? <div style={{ padding: 24, color: 'var(--text-tertiary)' }}>Loading…</div> : (
          <div style={{ marginTop: 12 }}>
            {d.autoDetail && (
              <div style={{ fontSize: 12.5, color: '#1D4ED8', background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 8, padding: '9px 12px', marginBottom: 12 }}>
                <i className="ti ti-calculator" style={{ marginRight: 6 }} />
                This is an AUTO row — {currencySymbol()}{money(d.autoDetail.amount) || '0.00'} is computed from the {d.autoDetail.source === 'salary' ? 'payroll snapshots' : d.autoDetail.source === 'sso' ? 'employer social-security contributions' : 'rental agreements (company landlords + PND.3 individual landlords)'} for this month, the same source the Half-year P&L uses. There are no invoices behind it.
              </div>
            )}
            {!d.lines.length && !d.manual.length && !d.autoDetail && (
              <div style={{ padding: 16, color: 'var(--text-tertiary)', fontSize: 12.5 }}>Nothing recorded in this cell.</div>
            )}
            {!!d.lines.length && (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr><th style={th2}>Date</th><th style={th2}>Invoice / receipt</th><th style={th2}>Company</th><th style={th2}>Tax ID</th><th style={{ ...th2, textAlign: 'right' }}>Amount</th></tr></thead>
                <tbody>
                  {d.lines.map((l: any) => (
                    <tr key={l.id} onClick={open} title="Open this month in Monthly Tax" style={{ cursor: 'pointer' }}>
                      <td style={td2}>{l.date || '—'}</td>
                      <td style={td2}>{l.docNo || '—'}</td>
                      <td style={td2}>{l.party || '—'}</td>
                      <td style={{ ...td2, fontSize: 11.5, color: 'var(--text-tertiary)' }}>{l.partyTaxId || '—'}</td>
                      <td style={numTd}>{money(l.amount) || '0.00'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {!!d.manual.length && (
              <div style={{ marginTop: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: '#92400E', marginBottom: 4 }}>MANUAL ENTRIES (no invoice in the system)</div>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <tbody>
                    {d.manual.map((m: any) => (
                      <tr key={m.id} style={{ background: '#FFFBEB' }}>
                        <td style={td2}>{m.note || '—'}</td>
                        <td style={{ ...td2, fontSize: 11.5, color: 'var(--text-tertiary)' }}>{m.byName || ''}</td>
                        <td style={numTd}>
                          {mEdit?.id === m.id ? (
                            <input autoFocus value={mEdit.amount} disabled={busy}
                              onChange={e => setMEdit({ id: m.id, amount: e.target.value })}
                              onKeyDown={e => { if (e.key === 'Enter') saveManual(m.id, mEdit.amount); if (e.key === 'Escape') setMEdit(null); }}
                              onBlur={() => saveManual(m.id, mEdit.amount)}
                              style={{ width: 116, boxSizing: 'border-box', padding: '2px 5px', textAlign: 'right', fontSize: 12.5,
                                fontVariantNumeric: 'tabular-nums', border: '1px solid var(--accent)', borderRadius: 5,
                                background: 'var(--bg-app)', color: 'var(--text-primary)' }} />
                          ) : (
                            <span onClick={() => setMEdit({ id: m.id, amount: String(m.amount) })} title="Click to change this amount"
                              style={{ cursor: 'pointer', borderBottom: '1px dashed var(--text-tertiary)' }}>{money(m.amount) || '0.00'}</span>
                          )}
                        </td>
                        <td style={{ ...td2, width: 30, textAlign: 'right' }}>
                          <button onClick={() => delManual(m)} disabled={busy} title="Delete this manual entry — use this once the real invoice reaches the system"
                            style={{ border: 'none', background: 'none', cursor: busy ? 'not-allowed' : 'pointer', color: '#c0453f', padding: 0 }}>
                            <i className="ti ti-trash" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 14, paddingTop: 10, borderTop: '2px solid var(--border-strong)' }}>
              <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
                {d.lines.length} invoice line(s){d.manual.length ? ` · ${d.manual.length} manual` : ''}{d.autoDetail ? ' · auto-calculated' : ''}
              </span>
              <span style={{ fontSize: 14, fontWeight: 800 }}>{currencySymbol()}{money(d.total) || '0.00'}</span>
            </div>
            {!!d.lines.length && (
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 6 }}>Click a line to open that month in Monthly Tax.</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Assign categories: per-partner defaults (firm-wide) + per-line overrides for the rest ────────
function AssignModal({ clientId, year, cats, onClose }: { clientId: string; year: number; cats: any[]; onClose: () => void }) {
  const [partners, setPartners] = useState<any[] | null>(null);
  const [lines, setLines] = useState<any[] | null>(null);
  const [overrides, setOverrides] = useState<any[] | null>(null);
  const [defs, setDefs] = useState<{ income: string; expense: string }>({ income: '', expense: '' });
  const [busy, setBusy] = useState('');
  const load = useCallback(() => {
    api.ebPartners(clientId, year).then((d: any) => setPartners(d.rows || [])).catch(() => setPartners([]));
    api.ebUnclassified(clientId, year).then((d: any) => setLines(d.rows || [])).catch(() => setLines([]));
    api.ebOverrides(clientId, year).then((d: any) => setOverrides(d.rows || [])).catch(() => setOverrides([]));
    api.ebClientDefaults(clientId).then((d: any) => setDefs(d.defaults || { income: '', expense: '' })).catch(() => { /* keep */ });
  }, [clientId, year]);
  useEffect(() => { load(); }, [load]);

  // Sortable By-partner table (owner 2026-08-18): Tax ID / Category / Expense / Income headers
  // toggle asc↔desc; null = the server's default order (biggest totals first). Sorting Category
  // ascending clusters the not-yet-assigned partners at the top.
  const [pSort, setPSort] = useState<null | { key: 'taxId' | 'category' | 'expense' | 'income'; dir: 1 | -1 }>(null);
  const catName = (id: string) => (cats.find((c: any) => c.id === id)?.name || '');
  const sortedPartners = React.useMemo(() => {
    if (!partners || !pSort) return partners;
    const { key, dir } = pSort;
    return [...partners].sort((a: any, b: any) => {
      const r = key === 'taxId' ? String(a.taxId).localeCompare(String(b.taxId))
        : key === 'category' ? catName(a.categoryId).localeCompare(catName(b.categoryId))
        : key === 'expense' ? (a.expenseTotal || 0) - (b.expenseTotal || 0)
        : (a.incomeTotal || 0) - (b.incomeTotal || 0);
      return r * dir;
    });
  }, [partners, pSort, cats]); // eslint-disable-line

  // Per-client fallback (owner 2026-08-18): every income line with no partner default and no line
  // override lands in this category — current AND future invoices. Set once per client.
  const setDefault = async (kind: 'income' | 'expense', categoryId: string) => {
    setBusy('def:' + kind);
    try { await api.ebSetClientDefault(clientId, kind, categoryId); load(); }
    catch (e: any) { window.alert(e?.message || 'Failed'); }
    finally { setBusy(''); }
  };

  // AUTO categories are excluded everywhere: their amounts are system-computed, so routing an
  // invoice line (or default) into them double-counts. The server rejects them too.
  const catOptions = (kind: string) => cats.filter(c => c.kind === kind && !c.auto_source);
  const assign = async (p: any, categoryId: string) => {
    if (!p.partnerId) { window.alert('This tax ID has no partner record yet — run "Import from VAT" in Business partners first.'); return; }
    setBusy(p.taxId);
    try { await api.ebAssign(p.partnerId, categoryId); load(); }
    catch (e: any) { window.alert(e?.message || 'Failed'); }
    finally { setBusy(''); }
  };
  const setLine = async (l: any, categoryId: string) => {
    setBusy(l.lineId);
    try { await api.ebLineCategory(clientId, l.lineId, categoryId); load(); }
    catch (e: any) { window.alert(e?.message || 'Failed'); }
    finally { setBusy(''); }
  };
  // Grouped pick (review 2026-08-18): groups are split per SIDE. Normally the pick writes the
  // firm-wide partner default — but when the partner already carries a default of the OTHER side
  // (someone we both buy from and sell to), only this group's lines get line-level overrides, so
  // the two sides never fight over the single firm-wide slot.
  const pickGroup = async (g: any, categoryId: string) => {
    if (!categoryId) return;
    const p = (partners || []).find((x: any) => x.taxId === g.taxId);
    if (!p?.partnerId) { window.alert('This tax ID has no partner record yet — run "Import from VAT" in Business partners first.'); return; }
    const curKind = p.categoryId ? (cats.find((c: any) => c.id === p.categoryId)?.kind || '') : '';
    setBusy(g.key);
    try {
      if (curKind && curKind !== g.side) {
        for (const l of g.items) await api.ebLineCategory(clientId, l.lineId, categoryId);
      } else {
        await api.ebAssign(p.partnerId, categoryId);
      }
      load();
    } catch (e: any) { window.alert(e?.message || 'Failed'); }
    finally { setBusy(''); }
  };

  // 10.5 → 11.5px (Calem, report 66c9d174, 2nd opening — see the breakdown table's th). Black, regular weight, 10px above
  // and below on a 16px line, white, over the darker --border-strong line (report 4bd4ae4e — see that th too).
  const th: React.CSSProperties = { padding: '10px 9px', fontSize: 11.5, fontWeight: 400, color: 'var(--text-primary)', lineHeight: '16px', textTransform: 'uppercase', borderBottom: '1px solid var(--border-strong)', textAlign: 'left', whiteSpace: 'nowrap', background: 'var(--bg-surface)' };
  const td: React.CSSProperties = { padding: '6px 9px', fontSize: 12.5, borderBottom: '1px solid var(--border)', verticalAlign: 'middle' };
  const selS: React.CSSProperties = { padding: '4px 7px', fontSize: 12, borderRadius: 7, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)', maxWidth: 230 };
  const m2 = (n: number) => Number(n || 0).toLocaleString(localeNumber(), { minimumFractionDigits: 2 });

  return (
    <div onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: 18 }}>
      <div style={{ width: 'min(940px, 100%)', maxHeight: '90vh', overflowY: 'auto', background: 'var(--bg-surface)', borderRadius: 14, border: '1px solid var(--border)', padding: 20, boxShadow: 'var(--shadow-md)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 14.5, fontWeight: 800 }}>Assign categories — {year}</div>
          <button onClick={onClose} style={{ width: 28, height: 28, borderRadius: 8, border: 'none', background: 'var(--c-gray-100)', color: 'var(--text-secondary)', cursor: 'pointer' }}><i className="ti ti-x" /></button>
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
          The partner default is FIRM-WIDE — every client's ledger classifies by it. A single invoice that needs a
          different category gets a line-level override in the list below.
        </div>

        {/* Per-client income fallback — a gym's walk-in sales are all one category, so pick it once. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12, padding: '10px 12px', borderRadius: 10, background: 'var(--bg-active)', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12.5, fontWeight: 800, color: 'var(--text-primary)' }}>Default income category</span>
          <select disabled={busy === 'def:income'} value={defs.income || ''} onChange={e => setDefault('income', e.target.value)} style={selS}>
            <option value="">— none (income stays unclassified) —</option>
            {catOptions('income').filter(c => !c.auto_source).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <span style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>
            THIS client only. Any income line with no partner default and no line override falls here — current and future invoices.
          </span>
        </div>

        <div style={{ fontSize: 12.5, fontWeight: 800, marginTop: 14, color: 'var(--text-primary)' }}>By partner (13-digit tax ID)</div>
        {!partners ? <div style={{ padding: 20, color: 'var(--text-tertiary)' }}>Loading…</div> : !partners.length ? (
          <div style={{ padding: 14, color: 'var(--text-tertiary)', fontSize: 12.5 }}>No ledger rows with a valid tax ID this year.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 6 }}>
            <thead><tr>
              {([['taxId', 'Tax ID', false], ['partner', 'Partner', false], ['expense', 'Expense', true], ['income', 'Income', true], ['category', 'Category', false]] as [string, string, boolean][]).map(([key, label, right]) => key === 'partner' ? (
                <th key={key} style={th}>{label}</th>
              ) : (
                <th key={key} title="Click to sort" onClick={() => setPSort(s => s && s.key === key ? { key: key as any, dir: (s.dir * -1) as 1 | -1 } : { key: key as any, dir: key === 'expense' || key === 'income' ? -1 : 1 })}
                  style={{ ...th, textAlign: right ? 'right' : 'left', cursor: 'pointer', userSelect: 'none' }}>
                  {label}{pSort?.key === key ? (pSort.dir === 1 ? ' ▲' : ' ▼') : ''}
                </th>
              ))}
            </tr></thead>
            <tbody>
              {(sortedPartners || partners).map(p => (
                <tr key={p.taxId}>
                  <td style={{ ...td, fontFamily: 'var(--font-mono, monospace)', fontSize: 11.5 }}>{p.taxId}</td>
                  <td style={td}>
                    {p.name || <span style={{ color: 'var(--text-tertiary)' }}>(not in partner DB)</span>}
                    {p.name && !p.verified && <span style={{ marginLeft: 5, fontSize: 9, fontWeight: 800, padding: '0 4px', borderRadius: 4, background: '#FEF3F2', color: '#B42318' }}>UNVERIFIED</span>}
                  </td>
                  <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{p.expenseTotal ? m2(p.expenseTotal) : ''}</td>
                  <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{p.incomeTotal ? m2(p.incomeTotal) : ''}</td>
                  <td style={td}>
                    <select disabled={busy === p.taxId} value={p.categoryId || ''} onChange={e => assign(p, e.target.value)} style={selS}>
                      <option value="">— not set —</option>
                      {/* a partner with both sections gets whichever side fits; mismatched side counts as unclassified */}
                      <optgroup label="Expense">{catOptions('expense').map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</optgroup>
                      <optgroup label="Income">{catOptions('income').map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</optgroup>
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Unclassified — same tax ID collapses to ONE row whose pick writes the partner default
            (owner 2026-08-18: "can items with the same Tax ID be grouped together?"). Lines with
            no tax ID stay individual (their pick is a line-level override). */}
        <div style={{ fontSize: 12.5, fontWeight: 800, marginTop: 18, color: 'var(--text-primary)' }}>
          Unclassified lines
          <span style={{ fontWeight: 400, color: 'var(--text-tertiary)', marginLeft: 6, fontSize: 11.5 }}>(no partner default, no line override — same tax ID grouped; one pick classifies them all)</span>
        </div>
        {!lines ? <div style={{ padding: 20, color: 'var(--text-tertiary)' }}>Loading…</div> : !lines.length ? (
          <div style={{ padding: 14, color: '#15803D', fontSize: 12.5 }}>✓ Everything is classified.</div>
        ) : (() => {
          // One group per (tax ID, side): mixing both directions into one row hid that a single
          // one-sided partner default can never classify both (review 2026-08-18).
          const groups = new Map<string, { key: string; taxId: string; side: 'income' | 'expense'; name: string; count: number; total: number; items: any[] }>();
          const singles: any[] = [];
          for (const l of lines) {
            if (!l.taxId) { singles.push(l); continue; }
            const side: 'income' | 'expense' = l.section === 'output' ? 'income' : 'expense';
            const key = `${l.taxId}:${side}`;
            let g = groups.get(key);
            if (!g) { g = { key, taxId: l.taxId, side, name: '', count: 0, total: 0, items: [] }; groups.set(key, g); }
            if (!g.name && l.partyName) g.name = l.partyName;
            g.count++; g.total += Number(l.base) || 0; g.items.push(l);
          }
          const pFor = (taxId: string) => (partners || []).find((p: any) => p.taxId === taxId);
          return (
            <>
              {groups.size > 0 && (
                <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 6 }}>
                  <thead><tr><th style={th}>Tax ID</th><th style={th}>Partner</th><th style={th}>Side</th><th style={{ ...th, textAlign: 'right' }}>Lines</th><th style={{ ...th, textAlign: 'right' }}>Amount</th><th style={th}>Category</th></tr></thead>
                  <tbody>
                    {Array.from(groups.values()).sort((a, b) => b.total - a.total).map(g => {
                      const p = pFor(g.taxId);
                      const curKind = p?.categoryId ? (cats.find((c: any) => c.id === p.categoryId)?.kind || '') : '';
                      const lineMode = !!(curKind && curKind !== g.side);
                      return (
                        <tr key={g.key}>
                          <td style={{ ...td, fontFamily: 'var(--font-mono, monospace)', fontSize: 11.5 }}>{g.taxId}</td>
                          <td style={{ ...td, maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p?.name || g.name || '—'}</td>
                          <td style={td}>
                            <span style={{ fontSize: 9.5, fontWeight: 800, padding: '1px 6px', borderRadius: 6, background: g.side === 'income' ? '#E3EFE8' : '#FEF3F2', color: g.side === 'income' ? '#276749' : '#B42318' }}>
                              {g.side === 'income' ? 'INCOME' : 'EXPENSE'}
                            </span>
                          </td>
                          <td style={{ ...td, textAlign: 'right' }}>{g.count}</td>
                          <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{m2(g.total)}</td>
                          <td style={td}>
                            <select disabled={busy === g.key || !partners} value="" onChange={e => pickGroup(g, e.target.value)} style={selS}>
                              <option value="">{partners ? '— pick —' : 'Loading…'}</option>
                              {catOptions(g.side).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                            {/* Which lever the pick pulls — the firm-wide default, or just these lines. */}
                            <div style={{ fontSize: 10, color: 'var(--text-tertiary)', marginTop: 2 }}>
                              {lineMode ? `partner default is ${curKind}-side — this pick classifies only these ${g.count} line(s)` : 'sets the firm-wide partner default'}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
              {singles.length > 0 && (
                <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: groups.size ? 12 : 6 }}>
                  <thead><tr><th style={th}>Month</th><th style={th}>Doc No</th><th style={th}>Party</th><th style={th}>Tax ID</th><th style={{ ...th, textAlign: 'right' }}>Amount</th><th style={th}>Category (this line only)</th></tr></thead>
                  <tbody>
                    {singles.map(l => (
                      <tr key={l.lineId}>
                        <td style={td}>{l.month}</td>
                        <td style={{ ...td, fontSize: 11.5 }}>{l.docNo || '—'}</td>
                        <td style={{ ...td, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.partyName || '—'}</td>
                        <td style={{ ...td, fontFamily: 'var(--font-mono, monospace)', fontSize: 11 }}><span style={{ color: '#B45309' }}>none</span></td>
                        <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{m2(l.base)}</td>
                        <td style={td}>
                          <select disabled={busy === l.lineId} value="" onChange={e => setLine(l, e.target.value)} style={selS}>
                            <option value="">— pick —</option>
                            {catOptions(l.section === 'output' ? 'income' : 'expense').map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          );
        })()}

        {/* Line-level overrides — visible and reversible, so a mis-pick is never permanent */}
        {overrides && overrides.length > 0 && (
          <>
            <div style={{ fontSize: 12.5, fontWeight: 800, marginTop: 18, color: 'var(--text-primary)' }}>
              Line-level overrides
              <span style={{ fontWeight: 400, color: 'var(--text-tertiary)', marginLeft: 6, fontSize: 11.5 }}>(these lines ignore the partner default — change or clear here)</span>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 6 }}>
              <thead><tr><th style={th}>Month</th><th style={th}>Doc No</th><th style={th}>Party</th><th style={{ ...th, textAlign: 'right' }}>Amount</th><th style={th}>Category</th></tr></thead>
              <tbody>
                {overrides.map(l => (
                  <tr key={l.lineId}>
                    <td style={td}>{l.month}</td>
                    <td style={{ ...td, fontSize: 11.5 }}>{l.docNo || '—'}</td>
                    <td style={{ ...td, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.partyName || '—'}</td>
                    <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{m2(l.base)}</td>
                    <td style={td}>
                      <select disabled={busy === l.lineId} value={l.categoryId} onChange={e => setLine(l, e.target.value)} style={selS}>
                        <option value="">— clear (use partner default) —</option>
                        {catOptions(l.section === 'output' ? 'income' : 'expense').map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );
}

// ── Category list manager — every tax operator, no extra permission (owner ruling ④) ─────────────
function CategoriesModal({ onClose }: { onClose: () => void }) {
  const [rows, setRows] = useState<any[]>([]);
  const [newName, setNewName] = useState(''); const [newKind, setNewKind] = useState('expense');
  const [editId, setEditId] = useState(''); const [editName, setEditName] = useState('');
  const [busy, setBusy] = useState(false);
  // Drag to reorder (owner 2026-08-19) — this list IS the row order of the breakdown grid. Dragging
  // is confined to one section: moving an income category among the expenses would change what side
  // its money is reported on, which a drag must never do.
  const [dragId, setDragId] = useState('');
  const [overId, setOverId] = useState('');
  const load = () => api.ebCategories().then((d: any) => setRows(d.rows || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const addCat = async () => {
    if (!newName.trim() || busy) return;
    setBusy(true);
    try { await api.ebCategoryCreate(newName.trim(), newKind); setNewName(''); load(); }
    catch (e: any) { window.alert(e?.message || 'Failed'); } finally { setBusy(false); }
  };
  const saveRename = async () => {
    if (!editName.trim() || busy) return;
    setBusy(true);
    try { await api.ebCategoryRename(editId, editName.trim()); setEditId(''); load(); }
    catch (e: any) { window.alert(e?.message || 'Failed'); } finally { setBusy(false); }
  };
  // Rhythm is stored on the CATEGORY (it is shared by every client that uses it), and only changes
  // how empty months are estimated — no recorded figure and no total is affected.
  const saveCadence = async (c: any, cadence: string) => {
    setRows(rs => rs.map(x => x.id === c.id ? { ...x, cadence } : x));
    try { await api.ebCategoryCadence(c.id, cadence); } catch (e: any) { alert(e.message); load(); }
  };
  const drop = async (target: any) => {
    const src = rows.find(r => r.id === dragId);
    setDragId(''); setOverId('');
    if (!src || !target || src.id === target.id || src.kind !== target.kind) return;
    // Rebuild the full list with the dragged row moved in front of the row it was dropped on, then
    // persist every id in the new order (the server writes sort_order 1..n).
    const next = rows.filter(r => r.id !== src.id);
    const at = next.findIndex(r => r.id === target.id);
    next.splice(at, 0, src);
    setRows(next);                       // optimistic — the list is small and the save is quick
    try { await api.ebCategoryReorder(next.map(r => r.id)); load(); }
    catch (e: any) { window.alert(e?.message || 'Failed to reorder'); load(); }
  };
  const del = async (c: any) => {
    if (!window.confirm(`Delete category "${c.name}"? Only possible when no data references it.`)) return;
    try { await api.ebCategoryDelete(c.id); load(); }
    catch (e: any) { window.alert(e?.message || 'Failed'); }
  };

  const inp: React.CSSProperties = { padding: '6px 9px', fontSize: 12.5, border: '1px solid var(--border-strong)', borderRadius: 7, background: 'var(--bg-app)', color: 'var(--text-primary)' };
  const row: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '5px 8px', borderBottom: '1px solid var(--border)', fontSize: 12.5 };

  const group = (kind: string, label: string) => (
    <>
      <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--text-tertiary)', textTransform: 'uppercase', margin: '12px 0 4px' }}>
        {label}<span style={{ marginLeft: 8, fontSize: 10, fontWeight: 600, textTransform: 'none', color: 'var(--text-tertiary)' }}>· drag to reorder — this is the row order on the breakdown</span>
      </div>
      {rows.filter(r => r.kind === kind).map(c => (
        <div key={c.id}
          draggable={!editId}
          onDragStart={e => { setDragId(c.id); e.dataTransfer.effectAllowed = 'move'; }}
          onDragOver={e => { if (dragId && rows.find(r => r.id === dragId)?.kind === c.kind) { e.preventDefault(); setOverId(c.id); } }}
          onDragLeave={() => setOverId(o => (o === c.id ? '' : o))}
          onDrop={e => { e.preventDefault(); drop(c); }}
          onDragEnd={() => { setDragId(''); setOverId(''); }}
          style={{ ...row, ...(overId === c.id ? { borderTop: '2px solid var(--accent)' } : {}), ...(dragId === c.id ? { opacity: 0.45 } : {}) }}>
          <i className="ti ti-grip-vertical" title="Drag to reorder" style={{ color: 'var(--text-tertiary)', cursor: 'grab', fontSize: 14 }} />
          {editId === c.id ? (
            <>
              <input value={editName} onChange={e => setEditName(e.target.value)} onKeyDown={e => e.key === 'Enter' && saveRename()} style={{ ...inp, flex: 1 }} autoFocus />
              <button onClick={saveRename} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#15803D' }}><i className="ti ti-check" /></button>
              <button onClick={() => setEditId('')} style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-tertiary)' }}><i className="ti ti-x" /></button>
            </>
          ) : (
            <>
              <span style={{ flex: 1 }}>{c.name}{c.auto_source && <span style={{ marginLeft: 5, fontSize: 9, fontWeight: 800, padding: '0 4px', borderRadius: 4, background: 'var(--accent-bg)', color: 'var(--accent-text)' }}>AUTO</span>}</span>
              {/* Projection rhythm (owner 2026-08-21). Shape alone cannot tell a monthly cost with one
                  booked invoice from a genuinely annual fee, so the operator says it once here and the
                  estimator stops guessing. Affects AI Projection only — never a recorded figure. */}
              <select value={c.cadence || ''} onChange={e => saveCadence(c, e.target.value)} onClick={e => e.stopPropagation()}
                title="How AI Projection should estimate this row's empty months"
                style={{ ...inp, width: 106, padding: '3px 5px', fontSize: 11.5, color: c.cadence ? 'var(--text-primary)' : 'var(--text-tertiary)' }}>
                <option value="">Auto</option>
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="yearly">Yearly</option>
                <option value="oneoff">One-off</option>
              </select>
              <button onClick={() => { setEditId(c.id); setEditName(c.name); }} title="Rename" style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-tertiary)' }}><i className="ti ti-pencil" /></button>
              {!c.auto_source && <button onClick={() => del(c)} title="Delete (only when empty)" style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#c0453f' }}><i className="ti ti-trash" /></button>}
            </>
          )}
        </div>
      ))}
    </>
  );

  return (
    <div onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: 18 }}>
      <div style={{ width: 'min(560px, 100%)', maxHeight: '88vh', overflowY: 'auto', background: 'var(--bg-surface)', borderRadius: 14, border: '1px solid var(--border)', padding: 20, boxShadow: 'var(--shadow-md)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 14.5, fontWeight: 800 }}>Categories</div>
          <button onClick={onClose} style={{ width: 28, height: 28, borderRadius: 8, border: 'none', background: 'var(--c-gray-100)', color: 'var(--text-secondary)', cursor: 'pointer' }}><i className="ti ti-x" /></button>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <input value={newName} onChange={e => setNewName(e.target.value)} onKeyDown={e => e.key === 'Enter' && addCat()} placeholder="New category name…" style={{ ...inp, flex: 1 }} />
          <select value={newKind} onChange={e => setNewKind(e.target.value)} style={inp}>
            <option value="expense">Expense</option><option value="income">Income</option>
          </select>
          <button onClick={addCat} disabled={busy || !newName.trim()} style={{ padding: '6px 13px', borderRadius: 8, border: 'none', background: 'var(--accent)', color: '#fff', fontWeight: 700, fontSize: 12.5, cursor: 'pointer', opacity: busy || !newName.trim() ? 0.6 : 1 }}>Add</button>
        </div>
        {group('income', 'Income')}
        {group('expense', 'Expense')}
        <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 10 }}>
          AUTO rows are system-calculated (payroll / SSO / rental) and cannot be deleted. A category holding data must be
          emptied (reassign its lines and partner defaults) before it can be deleted.
        </div>
      </div>
    </div>
  );
}

// ── Manual expense entries (owner 2026-08-18) ────────────────────────────────
// Bills that have no invoice in the system yet: month + expense category + amount + note.
// They live ONLY in this breakdown (never the VAT ledger, never the Half-year P&L) and must be
// deleted by hand when the real invoice arrives — the page banner repeats this warning.
function ManualEntriesModal({ clientId, year, cats, onClose }: { clientId: string; year: number; cats: any[]; onClose: () => void }) {
  const [rows, setRows] = useState<any[] | null>(null);
  const [busy, setBusy] = useState('');
  const [form, setForm] = useState<{ month: number; categoryId: string; amount: string; note: string }>({ month: new Date().getMonth() + 1, categoryId: '', amount: '', note: '' });
  const [editId, setEditId] = useState('');
  const [edit, setEdit] = useState<{ month: number; categoryId: string; amount: string; note: string }>({ month: 1, categoryId: '', amount: '', note: '' });

  const expCats = cats.filter(c => c.kind === 'expense' && !c.auto_source);
  const load = useCallback(() => {
    api.ebManual(clientId, year).then((d: any) => setRows(d.rows || [])).catch(() => setRows([]));
  }, [clientId, year]);
  useEffect(() => { load(); }, [load]);

  // Sortable list (owner 2026-08-19) — same interaction as the Assign-categories partner table:
  // click a header to sort, click again to flip; null = the server's default order (month asc).
  const [mSort, setMSort] = useState<null | { key: 'month' | 'category' | 'amount' | 'note' | 'by'; dir: 1 | -1 }>(null);
  const sortedRows = React.useMemo(() => {
    if (!rows || !mSort) return rows;
    const { key, dir } = mSort;
    return [...rows].sort((a: any, b: any) => {
      const r = key === 'month' ? a.month - b.month
        : key === 'category' ? String(a.categoryName || '').localeCompare(String(b.categoryName || ''))
        : key === 'amount' ? (Number(a.amount) || 0) - (Number(b.amount) || 0)
        : key === 'note' ? String(a.note || '').localeCompare(String(b.note || ''))
        : String(a.createdByName || '').localeCompare(String(b.createdByName || ''));
      return r * dir;
    });
  }, [rows, mSort]);

  const add = async () => {
    if (!form.categoryId || !form.amount) { window.alert('Pick a category and enter an amount.'); return; }
    setBusy('add');
    try { await api.ebManualAdd(clientId, { year, month: form.month, categoryId: form.categoryId, amount: Number(form.amount), note: form.note }); setForm(f => ({ ...f, amount: '', note: '' })); load(); }
    catch (e: any) { window.alert(e?.message || 'Failed'); }
    finally { setBusy(''); }
  };
  const saveEdit = async () => {
    setBusy(editId);
    try { await api.ebManualUpdate(editId, { month: edit.month, categoryId: edit.categoryId, amount: Number(edit.amount), note: edit.note }); setEditId(''); load(); }
    catch (e: any) { window.alert(e?.message || 'Failed'); }
    finally { setBusy(''); }
  };
  const del = async (r: any) => {
    if (!window.confirm(`Delete this manual entry (${currencySymbol()}${money(r.amount) || r.amount})?`)) return;
    setBusy(r.id);
    try { await api.ebManualDelete(r.id); load(); }
    catch (e: any) { window.alert(e?.message || 'Failed'); }
    finally { setBusy(''); }
  };

  // 10.5 → 11.5px (Calem, report 66c9d174, 2nd opening — see the breakdown table's th). Black, regular weight, 10px above
  // and below on a 16px line, white, over the darker --border-strong line (report 4bd4ae4e — see that th too).
  const th: React.CSSProperties = { padding: '10px 9px', fontSize: 11.5, fontWeight: 400, color: 'var(--text-primary)', lineHeight: '16px', textTransform: 'uppercase', borderBottom: '1px solid var(--border-strong)', textAlign: 'left', whiteSpace: 'nowrap', background: 'var(--bg-surface)' };
  const td: React.CSSProperties = { padding: '6px 9px', fontSize: 12.5, borderBottom: '1px solid var(--border)', verticalAlign: 'middle' };
  const inp: React.CSSProperties = { padding: '5px 8px', fontSize: 12.5, borderRadius: 7, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)', boxSizing: 'border-box' };
  const iconBtn: React.CSSProperties = { border: 'none', background: 'none', cursor: 'pointer', padding: 3 };
  const monthSel = (v: number, set: (m: number) => void) => (
    <select value={v} onChange={e => set(Number(e.target.value))} style={inp}>
      {MONTH_ABBR.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
    </select>
  );
  const catSel = (v: string, set: (c: string) => void) => (
    <select value={v} onChange={e => set(e.target.value)} style={{ ...inp, maxWidth: 230 }}>
      <option value="">— category —</option>
      {/* An entry whose category was deleted keeps its selection visible instead of silently
          showing the placeholder — the server refuses saving it until a live category is picked. */}
      {v && !expCats.some(c => c.id === v) && <option value={v}>(current category — deleted)</option>}
      {expCats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
    </select>
  );

  return (
    <div onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: 18 }}>
      <div style={{ width: 'min(860px, 100%)', maxHeight: '90vh', overflowY: 'auto', background: 'var(--bg-surface)', borderRadius: 14, border: '1px solid var(--border)', padding: 20, boxShadow: 'var(--shadow-md)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 14.5, fontWeight: 800 }}>Manual expense entries — {year}</div>
          <button onClick={onClose} style={{ width: 28, height: 28, borderRadius: 8, border: 'none', background: 'var(--c-gray-100)', color: 'var(--text-secondary)', cursor: 'pointer' }}><i className="ti ti-x" /></button>
        </div>
        <div style={{ fontSize: 12, color: '#92400E', background: '#FFFBEB', border: '1px solid #F1C232', borderRadius: 8, padding: '8px 12px', marginTop: 10 }}>
          These amounts exist ONLY in this breakdown — they are never part of the VAT ledger or the Half-year P&L.
          When the real invoice reaches the system, delete the entry here or the amount double-counts.
        </div>

        {/* Add */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
          {monthSel(form.month, m => setForm(f => ({ ...f, month: m })))}
          {catSel(form.categoryId, c => setForm(f => ({ ...f, categoryId: c })))}
          <input type="number" placeholder="Amount" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} style={{ ...inp, width: 120, textAlign: 'right' }} />
          <input placeholder="Note (optional)" value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} style={{ ...inp, flex: 1, minWidth: 160 }} />
          <button onClick={add} disabled={busy === 'add'} style={{ padding: '6px 14px', borderRadius: 8, border: 'none', background: 'var(--accent)', color: '#fff', fontWeight: 700, fontSize: 12.5, cursor: 'pointer', opacity: busy === 'add' ? 0.6 : 1 }}>
            <i className={busy === 'add' ? 'ti ti-loader' : 'ti ti-plus'} style={{ marginRight: 4 }} />Add
          </button>
        </div>

        {/* List */}
        {!rows ? <div style={{ padding: 20, color: 'var(--text-tertiary)' }}>Loading…</div> : !rows.length ? (
          <div style={{ padding: 16, color: 'var(--text-tertiary)', fontSize: 12.5 }}>No manual entries this year.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 12 }}>
            <thead><tr>
              {([['month', 'Month', false], ['category', 'Category', false], ['amount', 'Amount', true], ['note', 'Note', false], ['by', 'By', false]] as [string, string, boolean][]).map(([key, label, right]) => (
                <th key={key} title="Click to sort" onClick={() => setMSort(s => s && s.key === key ? { key: key as any, dir: (s.dir * -1) as 1 | -1 } : { key: key as any, dir: key === 'amount' ? -1 : 1 })}
                  style={{ ...th, textAlign: right ? 'right' : 'left', cursor: 'pointer', userSelect: 'none' }}>
                  {label}{mSort?.key === key ? (mSort.dir === 1 ? ' ▲' : ' ▼') : ''}
                </th>
              ))}
              <th style={th} />
            </tr></thead>
            <tbody>
              {(sortedRows || rows).map(r => editId === r.id ? (
                <tr key={r.id} style={{ background: 'var(--bg-active)' }}>
                  <td style={td}>{monthSel(edit.month, m => setEdit(x => ({ ...x, month: m })))}</td>
                  <td style={td}>{catSel(edit.categoryId, c => setEdit(x => ({ ...x, categoryId: c })))}</td>
                  <td style={{ ...td, textAlign: 'right' }}><input type="number" value={edit.amount} onChange={e => setEdit(x => ({ ...x, amount: e.target.value }))} style={{ ...inp, width: 110, textAlign: 'right' }} /></td>
                  <td style={td}><input value={edit.note} onChange={e => setEdit(x => ({ ...x, note: e.target.value }))} style={{ ...inp, width: '100%' }} /></td>
                  <td style={td} />
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    <button onClick={saveEdit} disabled={busy === r.id} title="Save" style={{ ...iconBtn, color: '#15803D' }}><i className="ti ti-check" /></button>
                    <button onClick={() => setEditId('')} title="Cancel" style={{ ...iconBtn, color: 'var(--text-tertiary)' }}><i className="ti ti-x" /></button>
                  </td>
                </tr>
              ) : (
                <tr key={r.id}>
                  <td style={td}>{MONTH_ABBR[r.month - 1]}</td>
                  <td style={td}>{r.categoryName}</td>
                  <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{money(r.amount) || r.amount}</td>
                  <td style={{ ...td, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.note}>{r.note || '—'}</td>
                  <td style={{ ...td, fontSize: 11, color: 'var(--text-tertiary)', whiteSpace: 'nowrap' }}>{r.createdByName}{r.updatedAt ? ' · ✎' : ''}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    <button onClick={() => { setEditId(r.id); setEdit({ month: r.month, categoryId: r.categoryId, amount: String(r.amount), note: r.note }); }} title="Edit" style={{ ...iconBtn, color: 'var(--text-secondary)' }}><i className="ti ti-pencil" /></button>
                    <button onClick={() => del(r)} disabled={busy === r.id} title="Delete" style={{ ...iconBtn, color: '#B42318' }}><i className={busy === r.id ? 'ti ti-loader' : 'ti ti-trash'} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
